import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "utils/base-query";

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: "userApi",
  tagTypes: ["User"],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getAllUsers: builder.query({
      query: (params) => {
        return {
          method: "GET",
          url: "/admin/getAllUsers",
          params: params,
        };
      },
    }),
    getExportUsers: builder.query({
      query: (params) => {
        return {
          method: "GET",
          url: "/admin/getExportUsers",
          params: params,
        };
      },
    }),
    enableWarmup: builder.mutation({
      query: (id) => {
        return {
          method: "PUT",
          url: `/admin/${id}/warmup/enable`,
        };
      },
    }),
    pauseWarmup: builder.mutation({
      query: (id) => {
        return {
          method: "PUT",
          url: `/admin/${id}/warmup/pause`,
        };
      },
    }),
    updateUserPassword: builder.mutation({
      query: ({id, ...rest}) => {
        return {
          method: "PUT",
          url: `/admin/updateUserPasswordByAdmin/${id}`,
          body: rest,
        };
      },
    }),
    getAllRequests: builder.query({
      query: (params) => {
        return {
          method: 'GET',
          url: '/admin/getAllRequests',
          params: params
        };
      }
    }),
    getAllEmailAnalytics: builder.query({
      query: (params) => {
        return {
          method: 'GET',
          url: '/admin/getAllEmailAnalytics',
          params: params
        };
      }
    }),
    getEmailAnalyticsChart: builder.mutation({
      query: (params) => {
        return {
          method: 'GET',
          url: '/admin/getEmailAnalyticsChart',
          params: params
        };
      }
    }),
    getAllRequestsChart: builder.mutation({
      query: (params) => {
        return {
          method: 'GET',
          url: '/admin/getAllRequestsChart',
          params: params
        };
      }
    }),
    getDeleteUser: builder.mutation({
      query: (userId) => {
        return {
          method: 'POST',
          url: '/admin/delete-user',
          body: userId
        };
      }
    }),
    getUserAppSumoPlanUpdate: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/admin/update-appsumo-plan',
          body: data
        };
      }
    }),
    addBlocklist : builder.mutation({
			query:({data})=> {
				return {
					method: "POST",
					url: "/admin/add-blocklist",
					body: data,
				}
			},
			invalidatesTags : ["BLOCKLIST"]
		}),
		getBlocklist : builder.mutation({
			query:({params})=> {
				return {
					method: "GET",
					url: "/admin/get-blocklist",
					params,
				}
			},
			providesTags: ["BLOCKLIST"],
		}),
		duplicateCheck: builder.mutation({
			query: (data) => {
				return {
					method: "POST",
					url: `/admin/duplicate`,
					body: data,
				};
			},
		}),
		deleteBlocklist: builder.mutation({
			query : (lists) => {
				return {
					method : "DELETE",
					url : "/admin/delete-blocklist",
					body : lists
				}
			},
			invalidatesTags : ["BLOCKLIST"]
		}),
    getAllWorkspaces: builder.mutation({
      query: ({params}) => {
        return {
          method: "GET",
          url: "/admin/workspaces",
          params: params,
        };
      },
    }),
    updateWorkspace: builder.mutation({
      query: ({ id, ...body }) => {
        return {
          method: "PUT",
          url: `/admin/workspaces/${id}`,
          body: body,
        };
      },
    }),
    updateUserEmail: builder.mutation({
      query: ({ id, email }) => ({
        url: `/admin/updateUserEmailByAdmin/${id}`,
        method: 'PUT',
        body: { email },
      }),
    }),
    getUserStandardPlan: builder.mutation({
      query: (id) => {
        return {
          method: 'PUT',
          url: `/admin/${id}/getUserCurrentPlan`,
        };
      },
    }),
    createStandardPlan: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: `/admin/createStandardPlan`,
          body: {
            ...data,
            workspace: data.selectedWorkspace,
            priceId: data.planId,
          },
        };
      },
    }),
    createNewPlan: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: `/admin/createNewPlan`,
          body: {
            ...data,
            workspace: data.selectedWorkspace,
            priceId: data.planId,
          },
        };
      },
    }),
    getAllPlans: builder.mutation({
      query: () => {
        return {
          method: 'GET',
          url: `/admin/getAllPlans`,
        };
      },
    }),
    getAllPartners: builder.query({
      query: (params) => {
        return {
          method: "GET",
          url: "/manage-partners/partners-list",
          params: params,
        };
      },
    }),
    getAllPartnerUsers: builder.query({
      query: (params) => {
        return {
          method: "GET",
          url: "/manage-partners/get-users-by-partnerId",
          params: params,
        };
      },
    }),
    updatePartnerUserPlan: builder.mutation({
      query: (body) => {
        return {
          method: "PUT",
          url: "/manage-partners/update-user-plan",
          body: body,
        };
      },
    }),
    getCustomPlans: builder.query({
      query: (params) => {
        return {
          method: "GET",
          url: "/manage-partners/get-plans-by-partnerId",
          params: params,
        };
      },
    }),
    configurePartnerAccount: builder.mutation({
      query: (body) => {
        return {
          method: "PUT",
          url: "/manage-partners/configure-partner-account",
          body: body,
        };
      },
    }),
    deleteLeads: builder.mutation({
      query: (body) => {
        return {
          method: "DELETE",
          url: "/admin/delete-leads",
          body
        }
      }
    }),
    getPartnerPlans: builder.query({
      query: () => {
        return {
          method: "GET",
          url: "/partner-plan/get-plans"
        };
      },
    }),
    updatePartnerPlan: builder.mutation({
      query: ({partnerId, planId}) => {
        return {
          method: "POST",
          url: `/partner-plan/partner/${partnerId}/assign-plan/${planId}`
        };
      },
    }),
    verifyUserEmail: builder.mutation({
      query: (id) => {
        return {
          method: "PUT",
          url: `/admin/${id}/verify-email`
        };
      },
    }),
    removePartnerPlan: builder.mutation({
      query: ({ partnerId }) => {
        return {
          method: "PUT",
          url: `/partner-plan/partner/remove-plan/${partnerId}`
        };
      },
    }),
    buyAddOnsForPartner : builder.mutation({
      query: (data) => {
        return {
          
          url: "partner-plan/buyAddOns",
          method: 'PUT',
          body: data
        }
      }
    }),
    getPartnerAddOns: builder.query({
      query: (id) => {
        return {
          method: "GET",
          url: `/partner-plan/partner-addons-data/${id}`
        };
      },
    }),
    buyAddons: builder.mutation({
      query: (body) => ({
        url: "partner-plan/buyAddOns",
        method: 'PUT',
        body
      })
    }),
    getUserCurrentWorkspace: builder.query({
      query: ({partnerId}) => {
        return {
          method: "GET",
          url: `/manage-partners/getCurrentWorkspace/${partnerId}`,
        };
      },
    }),
    enablePartnerWithSubUsers: builder.mutation({
      query: (body) => {
        return {
          method: "PUT",
          url: `/manage-partners/enable-partner-with-sub-users`,
          body 
        };
      },
    }),
    getAllUniqueQueryNames: builder.query({
      query: (workspaceId) => ({
        method: "GET",
        url: "/leads/saved/files/unique",
        params: { workspaceId },
      }),
    }),
    deleteLeadsByQueryHash: builder.mutation({
      query: (queryHash) => ({
        method: "DELETE",
        url: `/leads/delete-by-query-hash/${queryHash}`,
      }),
    }),
    togglePartnerAiSuperSearch: builder.mutation({
      query: (body) => ({
        method: "PUT",
        url: "/admin/partner/ai-super-search",
        body,
      }),
    }),
    getPartnerAiSuperSearch: builder.query({
      query: (partnerId) => ({
        method: "GET",
        url: "/admin/partner/ai-super-search",
        params: { partnerId },
      }),
    }),
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllUsersQuery,
  useDeleteLeadsMutation,
  useGetExportUsersQuery,
  useEnableWarmupMutation,
  useUpdateUserPasswordMutation,
  useGetAllRequestsQuery,
  useGetAllEmailAnalyticsQuery,
  useGetEmailAnalyticsChartMutation,
  useGetAllRequestsChartMutation,
  useGetDeleteUserMutation,
  useGetUserAppSumoPlanUpdateMutation,
  usePauseWarmupMutation,
  useAddBlocklistMutation,
  useDuplicateCheckMutation,
  useGetBlocklistMutation,
  useDeleteBlocklistMutation,
  useGetAllWorkspacesMutation,
  useUpdateWorkspaceMutation,
  useUpdateUserEmailMutation,
  useGetUserStandardPlanMutation,
  useCreateStandardPlanMutation,
  useCreateNewPlanMutation,
  useGetAllPlansMutation,
  useGetAllPartnersQuery,
  useGetAllPartnerUsersQuery,
  useLazyGetAllPartnerUsersQuery,
  useUpdatePartnerUserPlanMutation,
  useGetCustomPlansQuery,
  useConfigurePartnerAccountMutation,
  useGetPartnerPlansQuery,
  useUpdatePartnerPlanMutation,
  useBuyAddOnsForPartnerMutation,
  useLazyGetPartnerAddOnsQuery,
  useBuyAddonsMutation,
  useLazyGetUserCurrentWorkspaceQuery,
  useEnablePartnerWithSubUsersMutation,
  useGetAllUniqueQueryNamesQuery,
  useDeleteLeadsByQueryHashMutation,
  useVerifyUserEmailMutation,
  useRemovePartnerPlanMutation,
  useTogglePartnerAiSuperSearchMutation,
  useLazyGetPartnerAiSuperSearchQuery
} = userApi;
